export const validateImportData = (data, config, existingRecords = [], t) => {
  
  if (!data || !Array.isArray(data)) return { 
    validRows: [], invalidRows: [], duplicateRows: [],
    totalValid: 0, totalInvalid: 0, totalDuplicates: 0, isValid: true,
  };
  
  const validRows = [];
  const invalidRows = [];
  const duplicateRows = [];
  const seenKeys = new Set();

  const requiredFields = config.templateColumns
    .filter((col) => col.required)
    .map((col) => col.fieldKey || col.label);

  const headerToKey = {};
  config.templateColumns.forEach(col => {
    const translatedHeader = t ? t(col.label) : col.label;
    headerToKey[translatedHeader] = col.fieldKey || col.label;
  });

  const labelByField = {};
  config.templateColumns.forEach((col) => {
    labelByField[col.fieldKey || col.label] = t ? t(col.label) : col.label;
  });

  const tr = (key, params = {}) =>
    t ? t(key, { ...params, interpolation: { escapeValue: false } }) : key;

  data.forEach((row, index) => {
    const normalizedRow = {};
    for (const [header, value] of Object.entries(row)) {
      const key = headerToKey[header] || header;
      normalizedRow[key] = value;
    }

    const rowErrors = [];
    const mappedData = {};

    // 1. Required fields check 
    for (const field of requiredFields) {
      const value = normalizedRow[field];
      if (!value || (typeof value === "string" && !value.trim())) {
        if (!config.validationRules[field]) {
          rowErrors.push(tr("bulkImport.errors.required", { field: labelByField[field] || field }));
        }
      }
    }

    // 2. Validation rules check
    for (const [field, rules] of Object.entries(config.validationRules)) {
      const value = normalizedRow[field];
      const strValue = value != null ? String(value).trim() : "";
      const label = labelByField[field] || rules.label || field;

      // Required check
      if (rules.required && !strValue) {
        rowErrors.push(tr("bulkImport.errors.required", { field: label }));
        continue;
      }

      if (!strValue) continue; // optional + empty → skip

      const msg = tr(rules.message || "bulkImport.errors.invalidValue", rules.messageParams);

      // Pattern check
      if (rules.pattern && !rules.pattern.test(strValue)) {
        rowErrors.push(`${label}: ${msg}`);
      }

      // Custom validate fn
      if (rules.validate && !rules.validate(strValue, row)) {
        rowErrors.push(`${label}: ${msg}`);
      }

      mappedData[field] = rules.transform
        ? rules.transform(strValue)
        : strValue;
    }

    // 3. Copy remaining fields (non-validated optional ones)
    for (const col of config.templateColumns) {
      const key = col.fieldKey || col.label;
      if (!(key in mappedData) && normalizedRow[key] != null) {
        mappedData[key] = String(normalizedRow[key]).trim();
      }
    }

    // 3b. In-file duplicate check (same key fields appearing more than once)
    const keyFields = config.duplicateCheck?.fields;
    if (keyFields?.length) {
      const keyValues = keyFields.map((f) =>
        String(mappedData[f] ?? "").trim().toLowerCase()
      );
      if (keyValues.every(Boolean)) {
        const key = keyValues.join("|");
        if (seenKeys.has(key)) {
          const msg =
            config.duplicateCheck.inFileMessage ||
            "bulkImport.errors.duplicateInFile";
          rowErrors.push(t ? t(msg) : msg);
        } else {
          seenKeys.add(key);
        }
      }
    }

    // 4. Duplicate check
    let isDuplicate = false;
    let duplicateReason = "";

    if (config.duplicateCheck?.enabled && existingRecords.length > 0) {
      const duplicate = config.duplicateCheck.checkDuplicate(
        mappedData,
        existingRecords,
      );
      if (duplicate) {
        isDuplicate = true;
        duplicateReason = duplicate.reason || "Record already exists";
      }
    }

    if (rowErrors.length > 0) {
      invalidRows.push({
        rowNumber: row._rowNumber ?? index + 2,
        originalData: row,
        errors: rowErrors,
      });
    } else if (isDuplicate) {
      duplicateRows.push({
        rowNumber: row._rowNumber ?? index + 2,
        data: mappedData,
        displayData: mappedData,
        reason: duplicateReason,
      });
    } else {
      validRows.push({ rowNumber: row._rowNumber ?? index + 2, data: mappedData });
    }
  });

  return {
    validRows,
    invalidRows,
    duplicateRows,
    totalValid: validRows.length,
    totalInvalid: invalidRows.length,
    totalDuplicates: duplicateRows.length,
    isValid: invalidRows.length === 0,
  };
};
